/**
 * Measurement Conversion Helper
 * Converts decimal inch measurements to reduced fractions with 1/16 precision
 */

export interface MeasurementConversionResult {
  whole: number;
  fraction: string;
  displayValue: string;
}

/**
 * Converts a decimal measurement (in inches) to whole inches + reduced fraction format
 *
 * Algorithm:
 * 1. Extract whole inches
 * 2. Convert decimal remainder to /16 precision
 * 3. Round DOWN to nearest allowed tape-measure fraction
 * 4. Append "+" if the value was rounded down
 *
 * Allowed fractions: 1/8, 1/4, 3/8, 1/2, 5/8, 3/4, 7/8
 *
 * @param decimalValue - The decimal measurement in inches (e.g., 12.3125)
 * @returns MeasurementConversionResult with whole, fraction, and display value
 *
 * @example
 * convertDecimalToFraction(12.3125) // Returns: { whole: 12, fraction: "1/4+", displayValue: "12 1/4+" }
 * convertDecimalToFraction(12.5)    // Returns: { whole: 12, fraction: "1/2", displayValue: "12 1/2" }
 * convertDecimalToFraction(12.0625) // Returns: { whole: 12, fraction: "+", displayValue: "12+" }
 */
export function convertDecimalToFraction(decimalValue: number): MeasurementConversionResult {
  // Step 1: Extract whole inches
  const whole = Math.floor(decimalValue);

  // Step 2: Get decimal remainder
  const remainder = decimalValue - whole;

  // Step 3: Convert to /16 precision
  const sixteenth = Math.floor(remainder * 16);

  // Step 4: Map to allowed fractions (round DOWN)
  let fraction = '';
  let needsPlus = false;

  if (sixteenth >= 14) {
    fraction = '7/8';
    needsPlus = sixteenth > 14;
  } else if (sixteenth >= 12) {
    fraction = '3/4';
    needsPlus = sixteenth > 12;
  } else if (sixteenth >= 10) {
    fraction = '5/8';
    needsPlus = sixteenth > 10;
  } else if (sixteenth >= 8) {
    fraction = '1/2';
    needsPlus = sixteenth > 8;
  } else if (sixteenth >= 6) {
    fraction = '3/8';
    needsPlus = sixteenth > 6;
  } else if (sixteenth >= 4) {
    fraction = '1/4';
    needsPlus = sixteenth > 4;
  } else if (sixteenth >= 2) {
    fraction = '1/8';
    needsPlus = sixteenth > 2;
  } else if (sixteenth >= 1) {
    fraction = '';
    needsPlus = true; // Just "+" for values like 1/16
  } else {
    // sixteenth === 0, no fraction needed
    fraction = '';
    needsPlus = false;
  }

  // Append "+" if we rounded down
  if (needsPlus) {
    fraction = fraction ? `${fraction}+` : '+';
  }

  // Build display value
  const displayValue = fraction ? `${whole} ${fraction}` : `${whole}`;

  return {
    whole,
    fraction,
    displayValue: displayValue.trim(),
  };
}

/**
 * Checks if a field is a measurement field that needs auto-conversion
 * Based on field title patterns
 */
export function isMeasurementField(fieldTitle: string): boolean {
  const normalizedTitle = (fieldTitle || '').toLowerCase().trim();
  return (
    normalizedTitle.includes('w (inches)') ||
    normalizedTitle.includes('h (inches)') ||
    normalizedTitle === 'w (inches)' ||
    normalizedTitle === 'h (inches)'
  );
}

/**
 * Checks if the current template requires laser measurement conversion
 * For now, this is a placeholder that can be configured based on:
 * - Template name
 * - Document ID
 * - App ID
 * - Custom field metadata
 */
export function shouldEnableLaserConversion(templateName?: string, documentId?: string, appId?: string): boolean {
  // Check for "Laser Presentation Proposal Template" by name
  if (templateName && templateName.toLowerCase().includes('laser presentation proposal')) {
    return true;
  }

  // TODO: Add additional checks as needed
  // - Specific document IDs
  // - Specific app IDs
  // - Custom metadata flags

  return false;
}
